import type { GeocodingResult } from '@iheartjane/geolocation';
import { EventNames } from '@iheartjane/tracking';

import { fetchUserPreferredStoreIds } from '@jane/shared-ecomm/data-access';
import { track } from '@jane/shared-ecomm/tracking';
import type { UserPreferences } from '@jane/shared/types';

import type { LocationErrors } from './unservedLocationModalContent';

const illegalStates = ['ID', 'KS', 'NC', 'NE', 'SC', 'WY'];

const additionalRegulationStates = ['FL', 'PA', 'AB', 'KY'];

export const validateUserPreferences = async (
  location: GeocodingResult,
  userPreferences: UserPreferences
): Promise<{ error: LocationErrors | null }> => {
  track({ event: EventNames.SetLocation, ...location });

  const { coordinates, city, cityState, state, street, zipcode } = location;

  const {
    storeFulfillmentType,
    storeAvailability,
    storeType,
    storeSearchRadius,
  } = userPreferences;
  const delivery = storeFulfillmentType === 'delivery';

  if (!city || cityState === 'Unknown') {
    return { error: 'noCity' };
  }
  if (delivery && !street) {
    return { error: 'deliveryAddress' };
  }

  if (illegalStates.includes(state!)) {
    return { error: 'illegal' };
  }

  if (additionalRegulationStates.includes(state!)) {
    return { error: 'regulations' };
  }

  try {
    const stores = await fetchUserPreferredStoreIds({
      coordinates,
      zipcode,
      userPreferences: {
        storeFulfillmentType,
      },
    });

    if (stores?.length) {
      if (!storeAvailability && !storeType && !storeSearchRadius) {
        return { error: null };
      }

      const storesByAllPreferences = await fetchUserPreferredStoreIds({
        coordinates,
        zipcode,
        userPreferences,
      });

      if (storesByAllPreferences?.length) {
        return { error: null };
      } else {
        return { error: 'noStoresInSettings' };
      }
    } else {
      return { error: delivery ? 'noDeliveryStores' : 'noPickupStores' };
    }
  } catch {
    return {
      error: 'error',
    };
  }
};
